import { useEffect, useId, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import MarketingLayout from "@/components/marketing/MarketingLayout";

export type TocItem = {
  num: string;
  title: string;
  id: string;
};

export type LegalSection = {
  num: string;
  title: string;
  id: string;
  body: React.ReactNode;
  /** Show "Back to top ↑" link after this section */
  backToTop?: boolean;
};

export interface LegalPageLayoutProps {
  eyebrow: string;
  heading: string;
  effectiveLine: string;
  tocItems: TocItem[];
  sections: LegalSection[];
}

const STICKY_TOP_PX = 96; // 72px sticky header + spacing

/**
 * Shared layout for /privacy and /terms in the marketing look: header block,
 * a sticky contents list (a collapsible "Contents" panel below 760px) and the
 * numbered sections. Section bodies keep their own markup; `.mk-legal-body`
 * styles the paragraphs, subsections, definition lists and callouts.
 */
const LegalPageLayout = ({ eyebrow, heading, effectiveLine, tocItems, sections }: LegalPageLayoutProps) => {
  const [activeId, setActiveId] = useState<string>(tocItems[0]?.id ?? "");
  const [contentsOpen, setContentsOpen] = useState(false);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const contentsId = useId();

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost intersecting section.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: `-${STICKY_TOP_PX}px 0px -55% 0px`, threshold: 0 },
    );
    sectionRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    if (el) sectionRefs.current.set(id, el);
    else sectionRefs.current.delete(id);
  };

  const tocList = (mobile: boolean) =>
    tocItems.map((item) => (
      <li key={item.id}>
        <a
          href={`#${item.id}`}
          aria-current={item.id === activeId ? "true" : undefined}
          onClick={mobile ? () => setContentsOpen(false) : undefined}
        >
          <span>{item.num}</span>
          <span>{item.title}</span>
        </a>
      </li>
    ));

  return (
    <MarketingLayout>
      <div className="mk-container">
        <header
          className="mk-stack"
          style={{
            padding: "clamp(56px,8cqi,112px) 0 clamp(40px,5cqi,64px)",
            gap: 20,
            borderBottom: "1px solid var(--mk-border)",
          }}
        >
          <span className="mk-label">{eyebrow}</span>
          <h1 className="mk-h1">
            {heading}
          </h1>
          <span style={{ font: "400 var(--mk-text-meta)/1.3 var(--mk-mono)", color: "var(--mk-ink-2)" }}>{effectiveLine}</span>
        </header>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "32px clamp(48px,6cqi,96px)",
            padding: "clamp(32px,5cqi,64px) 0 clamp(80px,10cqi,128px)",
            alignItems: "flex-start",
          }}
        >
          <nav
            aria-label="Contents"
            className="mk-legal-nav-desktop mk-stack"
            style={{ flex: "0 0 260px", position: "sticky", top: 120, gap: 12, paddingTop: 40 }}
          >
            <span style={{ font: "600 var(--mk-text-h4)/1.2 var(--mk-sans)" }}>Contents</span>
            <ol className="mk-legal-toc mk-stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {tocList(false)}
            </ol>
          </nav>

          <div className="mk-legal-nav-mobile mk-card" style={{ flex: "1 1 100%" }}>
            <button
              type="button"
              aria-expanded={contentsOpen}
              aria-controls={contentsId}
              onClick={() => setContentsOpen((open) => !open)}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                minHeight: 52,
                padding: "0 16px",
                background: "transparent",
                border: 0,
                cursor: "pointer",
                font: "600 var(--mk-text-btn)/1 var(--mk-sans)",
                color: "var(--mk-ink)",
              }}
            >
              Contents
              <ChevronDown
                size={20}
                color="var(--mk-primary)"
                aria-hidden="true"
                style={{ transition: "transform 200ms", transform: contentsOpen ? "rotate(180deg)" : "none" }}
              />
            </button>
            <nav aria-label="Contents" id={contentsId} hidden={!contentsOpen}>
              <ol
                className="mk-legal-toc mk-legal-toc--mobile mk-stack"
                style={{ listStyle: "none", margin: 0, padding: "4px 16px 12px", borderTop: "1px solid var(--mk-border)" }}
              >
                {tocList(true)}
              </ol>
            </nav>
          </div>

          <div className="mk-stack" style={{ flex: "1 1 560px", minWidth: 0, maxWidth: 760 }}>
            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                ref={setSectionRef(section.id)}
                aria-labelledby={`${section.id}-title`}
                className="mk-stack"
                style={{
                  scrollMarginTop: 120,
                  padding: "40px 0",
                  borderBottom: "1px solid var(--mk-border)",
                  gap: 20,
                }}
              >
                <span style={{ font: "500 var(--mk-text-meta)/1.3 var(--mk-mono)", color: "var(--mk-primary)" }}>{section.num}</span>
                <h2 id={`${section.id}-title`} style={{ font: "600 var(--mk-text-h2-sm)/1.15 var(--mk-sans)" }}>
                  {section.title}
                </h2>
                <div className="mk-legal-body">{section.body}</div>
                {section.backToTop && (
                  <a
                    href="#top"
                    style={{
                      alignSelf: "flex-start",
                      font: "500 var(--mk-text-small)/1 var(--mk-sans)",
                      color: "var(--mk-primary)",
                      textDecoration: "none",
                    }}
                  >
                    Back to top ↑
                  </a>
                )}
              </section>
            ))}
          </div>
        </div>
      </div>
    </MarketingLayout>
  );
};

export default LegalPageLayout;
